<template>
  <div>
    <b-container class="wide">
      <!-- Show head-block for the current step -->
      <head-block :title="currentStepTitle" />
      <b-row
        v-if="bookingsInfo.length && !paymentSuccessful"
        class="align-items-center mb-3 mt-2 no-margin-left ml-3 mr-3"
      >
        <b-col>
          <div class="d-flex justify-content-between align-items-center bg-light border rounded px-3 py-2 shadow-sm">
            <!-- Booking timer -->
            <div class="d-flex align-items-center font-weight-bold text-dark">
              <booking-timer
                class="ml-2"
                :bookingsInfo="bookingsInfo"
                :expirationSeconds="bookingTimerWindow"
                @expired="handleBookingExpiration"
              />
            </div>

            <!-- Checkout button or reserved space for invisible button -->
            <div>
              <b-button
                v-if="!selectedBookingsInfo.length && !isBookingExpired"
                size="sm"
                variant="success"
                @click="goToCheckoutDirectly"
              >
                <b-icon icon="credit-card" class="mr-1" />
                {{ $t('pages.new_booking.go_to_checkout') }}
              </b-button>
              <b-button
                v-else
                size="sm"
                variant="light"
                class="invisible"
              >
                <b-icon icon="credit-card" class="mr-1" />
                {{ $t('pages.new_booking.go_to_checkout') }}
              </b-button>
            </div>
          </div>
        </b-col>
      </b-row>

      <!-- Components based on step -->
      <select-shelf-period-price-component
        v-if="!selectedShelfBookingPrice && !selectedBookingsInfo.length && shelfPeriodPrices.length && feeData && market && !paymentSuccessful"
        :fee="feeData.fee"
        :market="market"
        :shelfPeriodPrices="shelfPeriodPrices"
        :isCustomerBooking="isCustomerView"
        :customerEmail="selectedBookingRequest.customerEmail"
        @shelfPeriodPriceSelected="selectShelfBookingPrice"
        @backToShelfTypes="isCustomerView ? $router.push({name: 'bookings'}) : $router.push({name: 'user-login', params: { marketId: market.marketId }})"
      />
      <select-additional-services-component
        v-if="selectedShelfBookingPrice && !selectedBookingsInfo.length && shelfTypeServices.length && !servicesStepCompleted && market && !paymentSuccessful"
        :shelfTypeServices="shelfTypeServices"
        :isCustomerBooking="isCustomerView"
        :market="market"
        @serviceSelected="selectShelfTypeServices"
        @backToShelfPeriodPrices="goBackToShelfPeriodPrices"
        @skipStep="skipServicesStep"
      />
      <booking-checkout-component
        v-if="selectedShelfBookingPrice && selectedBookingsInfo.length && servicesStepCompleted && !paymentSuccessful"
        :market="market"
        :bookingsInfo="$_.cloneDeep(selectedBookingsInfo)"
        :isCustomerBooking="isCustomerView"
        :isBookingExtension="true"
        @paymentSuccessful="confirmPayment"
        @backToBookingCalendar="goToBookingRequests"
        @bookingDeleted="handleDeleteBooking"
      />
      <div class="mt-5 text-secondary" v-if="paymentSuccessful">
        <h3 class="text-center">{{ $t('notifications.text.create_booking.success_booking_confirmed') }}. {{ $t('notifications.text.create_booking.success_countdown_redirection', { countDown: countDown }) }}</h3>
      </div>
    </b-container>
  </div>
</template>

<script>

  import { mapState } from 'vuex'
  import countDownTimerMixin from "@base/mixins/CountDownTimerMixin.vue";
  import {
    BOOKING_REQUEST_EXTENSION_TYPE,
    REDIRECT_COUNTDOWN_SECONDS,
    BOOKING_RESERVE_EXPIRY_SECONDS,
    BOOKING_PAYMENT_EXPIRY_SECONDS,
  } from "@base/configs";
  import BookingCheckoutComponent from "@base/components/booking/BookingCheckoutComponent.vue";
  import BookingTimer from "@base/components/booking/BookingTimer.vue";
  import SelectShelfPeriodPriceComponent from "@base/components/booking/SelectShelfPeriodPriceComponent.vue";
  import DiscountUtilsMixin from "@base/mixins/DiscountUtilsMixin.vue";
  import BookingPeriodLabelMixin from "@base/mixins/BookingPeriodLabelMixin.vue";
  import HeadBlock from "@base/components/HeadBlock.vue";
  import moment from "moment/moment";
  import SelectAdditionalServicesComponent from "@base/components/booking/SelectAdditionalServicesComponent.vue";

  export default {
    name: 'ExtendBookingView',
    components: {
      SelectAdditionalServicesComponent,
      HeadBlock,
      BookingTimer, BookingCheckoutComponent, SelectShelfPeriodPriceComponent },

    mixins: [countDownTimerMixin, DiscountUtilsMixin, BookingPeriodLabelMixin],

    data () {
      return {
        loading: false,
        cartToken: null,
        selectedShelfBookingPrice: null,
        shelfPeriodPriceToCheck: null,
        bookingsInfo: [],
        selectedBookingsInfo: [],
        selectedServices: [],
        servicesStepCompleted: false,
        isBookingExpired: false,
        paymentSuccessful: false,
        countDown: REDIRECT_COUNTDOWN_SECONDS,
      }
    },

    created () {
      this.loading = true
      this.loader = this.$loading.show(this.$_loaderOptions)
      this.getShelfPeriodPrices()
        .then(this.getAdditionalServices)
          .finally(() => {
            this.getExtendBookingsInfo()
            this.loader.hide()
            this.loading = false
          })
      this.getCartToken()
    },

    props: {
      isCustomerView: {
        type: Boolean,
        default: false
      }
    },

    computed: {
      ...mapState({
        shelfPeriodPrices: state => state.shelfPeriodPrices,
        selectedBookingRequest: state => state.selectedBookingRequest,
        shelfTypeServices: state => state.shelfTypeServices,
        shelfTypeAvailability: state => state.shelfTypeAvailability,
        shelfTypes: state => state.shelfTypes,
        feeData () {
          return this.isCustomerView
            ? this.$store.state.customerData
            : this.$store.state.anonMarket
        },
        market () {
          return this.isCustomerView
            ? this.$store.state.userMarket
            : this.$store.state.anonMarket
        },
      }),
      currentStepTitle () {
        if (!this.selectedShelfBookingPrice && !this.selectedBookingsInfo.length && !this.paymentSuccessful) {
          return this.$t('title.customer.extend_booking_1_period_prices')
        } else if (this.selectedShelfBookingPrice && !this.selectedBookingsInfo.length && !this.servicesStepCompleted && this.shelfTypeServices.length) {
          return this.$t('title.customer.extend_booking_1_services')
        } else if (this.selectedShelfBookingPrice && this.selectedBookingsInfo.length && !this.paymentSuccessful) {
          return this.$t('title.customer.extend_booking_2')
        }
        return ''
      },

      bookingTimerWindow () {
        // A started payment holds the slot longer (matches the backend hold window).
        return localStorage.getItem('extendPaymentId')
          ? BOOKING_PAYMENT_EXPIRY_SECONDS
          : BOOKING_RESERVE_EXPIRY_SECONDS
      },

      totalBookingDays () {
        let totalDays = 0
        if (this.shelfPeriodPriceToCheck) {
          if (this.shelfPeriodPriceToCheck.bookingPeriod.days) {
            totalDays += this.shelfPeriodPriceToCheck.bookingPeriod.days
          }
          if (this.shelfPeriodPriceToCheck.bookingPeriod.weeks) {
            totalDays += this.shelfPeriodPriceToCheck.bookingPeriod.weeks * 7
          }
          if (this.shelfPeriodPriceToCheck.bookingPeriod.months) {
            totalDays += this.shelfPeriodPriceToCheck.bookingPeriod.months * 30
          }
          if (totalDays)
            totalDays -= 1
        }
        return totalDays
      },

      shelfAvailabilityPayload () {
        return {
          shelfPeriodPrice: this.shelfPeriodPriceToCheck ? this.shelfPeriodPriceToCheck.id : null,
          dateStart: moment(this.selectedBookingRequest.dateEnd).add(1, 'days').format('YYYY-MM-DD'),
          dateEnd: moment(this.selectedBookingRequest.dateEnd).add(1 + this.totalBookingDays, 'days').format('YYYY-MM-DD'),
          fullRange: true,
        }
      }
    },

    methods: {
      getShelfPeriodPrices () {
        return new Promise((resolve) => {
          const action = this.isCustomerView
            ? this.$_actionTypes.GET_SHELF_PERIOD_PRICES
            : this.$_actionTypes.GET_SHELF_PERIOD_PRICES_ANON
          this.$store.dispatch(action, this.selectedBookingRequest.shelfType.id)
            .catch((error) => {
              this.$_notifyError(error, this)
            })
            .finally(() => {
              resolve()
            })
        })
      },
      selectShelfBookingPrice (shelfPeriodPriceObj) {
        this.selectedShelfBookingPrice = shelfPeriodPriceObj
        if (!this.shelfTypeServices.length || this.servicesStepCompleted) {
          this.servicesStepCompleted = true
          this.validateDataAndReserve()
        }
      },

      getAdditionalServices () {
        return new Promise((resolve) => {
          const action = (this.isCustomerView) ? this.$_actionTypes.GET_SHELF_TYPE_SERVICES :
          this.$_actionTypes.GET_SHELF_TYPE_SERVICES_ANON
          this.$store.dispatch(action, this.selectedBookingRequest.shelfType.id)
            .catch((error) => {
              this.$_notifyError(error, this)
            })
            .finally(() => {
              resolve()
            })
        })
      },

      selectShelfTypeServices (serviceList) {
        this.selectedServices = serviceList
        this.servicesStepCompleted = true
        this.validateDataAndReserve()
      },

      skipServicesStep () {
        this.servicesStepCompleted = true
        this.validateDataAndReserve()
      },

      goBackToShelfPeriodPrices () {
        this.selectedServices = []
        this.servicesStepCompleted = false
        if (!this.shelfTypeServices.length) {
          this.selectedShelfBookingPrice = null
        }
      },

      validateDataAndReserve () {
        const existingShelfPeriodPrice = this.bookingsInfo.find(obj => obj.shelfPeriodPrice === this.selectedShelfBookingPrice.id)

        if (existingShelfPeriodPrice) {
          this.selectedBookingsInfo = this.bookingsInfo
          return
        }

        this.shelfPeriodPriceToCheck = this.selectedShelfBookingPrice
        this.getShelfTypeAvailability()
          .then(() => {
            if (!this.shelfTypeAvailability.isAvailable) {
              this.$notify({
                group: 'app',
                type: 'error',
                title: this.$t('notifications.title.error'),
                text: this.$t(
                  'notifications.text.booking_requests.error_booking_period_unavailable'
                ),
              })
            } else {
              const bookingInfo = this.constructBookingInfo()

              this.reserveBooking(bookingInfo)
                .then(() => {
                  localStorage.setItem("extendBookingsInfo", JSON.stringify(this.bookingsInfo))
                })
            }
          })
      },

      constructBookingInfo () {
        const startDate = moment(this.shelfAvailabilityPayload.dateStart)
        const endDate = moment(this.shelfAvailabilityPayload.dateEnd)
        const dateRange = [startDate.format('YYYY-MM-DD')];
        const eligibleClosingDays = []

        while (!startDate.isSame(endDate, 'day')) {
          startDate.add(1, 'days');
          dateRange.push(startDate.format('YYYY-MM-DD'));
        }

        for (let specialClosingDate of this.shelfTypeAvailability.specialClosingDates) {
          // We have a closing day during booking period - add one more day to period
          if (dateRange.includes(specialClosingDate)) {
            endDate.add(1, 'days')
            eligibleClosingDays.push(specialClosingDate)
          }
        }
        return {
          "parentBookingRequest": this.selectedBookingRequest.id,
          "type": BOOKING_REQUEST_EXTENSION_TYPE,
          "dateStartCustomer": this.shelfAvailabilityPayload.dateStart,
          "shelfTypeName": this.selectedShelfBookingPrice.shelfType.name,
          "bookingPeriodLabel": this.bookingPeriodLabel(this.selectedShelfBookingPrice.bookingPeriod, false),
          "shelfPeriodPrice": this.selectedShelfBookingPrice.id,
          "discountPercent": this.selectedShelfBookingPrice.discountPercent,
          "discountedPrice": this.computeDiscountedPrice(
              this.selectedShelfBookingPrice.price,
              this.selectedShelfBookingPrice.discountPercent,
              this.selectedShelfBookingPrice.discountValidFrom,
              this.selectedShelfBookingPrice.discountValidTo,
          ) || this.selectedShelfBookingPrice.price,
          "isDiscountActive": this.isDiscountActive(
              this.selectedShelfBookingPrice.discountPercent,
              this.selectedShelfBookingPrice.discountValidFrom,
              this.selectedShelfBookingPrice.discountValidTo),
          "originalPrice": this.selectedShelfBookingPrice.price,
          "dateEndCustomer": this.shelfAvailabilityPayload.dateEnd,
          "eligibleClosingDays": eligibleClosingDays,
          "expiresOn": moment().add(15, 'minutes').toDate().getTime(),
          "cartToken": this.cartToken,
          "bookingAddons": this.selectedServices,
          ...this.$route.query
        }
      },

      getShelfTypeAvailability () {
        return new Promise((resolve) => {
          if (this.loading)
            return
          this.loading = true
          this.loader = this.$loading.show(this.$_loaderOptions)
          const action = this.isCustomerView
            ? this.$_actionTypes.GET_SHELF_TYPE_AVAILABILITY
            : this.$_actionTypes.GET_SHELF_TYPE_AVAILABILITY_ANON
          this.$store.dispatch(action, this.shelfAvailabilityPayload)
            .then(() => {
              resolve()
            })
            .catch((error) => {
              this.$_notifyError(error, this)
            })
            .finally(() => {
              this.loader.hide()
              this.loading = false
              resolve()
            })
        })
      },

      reserveBooking (bookingInfo) {
        return new Promise((resolve) => {
          const action = this.isCustomerView
            ? this.$_actionTypes.BOOKING_REQUEST_RESERVE
            : this.$_actionTypes.BOOKING_REQUEST_RESERVE_ANON
          this.$store.dispatch(action, bookingInfo)
            .then((data) => {
              bookingInfo["bookingRequest"] = data.id
              bookingInfo["cartToken"] = data.cartToken
              bookingInfo["reservedAt"] = data.reservedTs
              this.bookingsInfo.push(bookingInfo)
              this.selectedBookingsInfo = this.bookingsInfo
              localStorage.setItem("extendBookingsInfo", JSON.stringify(this.bookingsInfo))
              resolve()
            })
            .catch((error) => {
              this.$_notifyError(error, this)
            })
        })
      },

      goToBookingRequests () {
        this.isCustomerView ? this.$router.push({name: 'bookings'}) : this.$router.push({name: 'user-login', params: { marketId: this.market.marketId }})
      },

      confirmBookings (objs) {
        this.selectedBookingsInfo = objs
        this.bookingsInfo = objs
      },

      confirmPayment () {
        this.paymentSuccessful = true
        this.handleBookingExpiration()
        this.bookingsInfo = []
        this.countDownTimer(this.countDown, this, this.isCustomerView ? {name: 'bookings'} : {name: 'user-login', params: { marketId: this.market.marketId }})
      },

      handleBookingExpiration () {
        localStorage.removeItem("extendBookingsInfo")
        localStorage.removeItem("extendCartToken")
        localStorage.removeItem("extendPaymentId")
        this.selectedShelfBookingPrice = null
        this.selectedBookingsInfo = []
        this.servicesStepCompleted = false
        this.selectedServices = []
        this.isBookingExpired = true
      },

      handleDeleteBooking (bookingsInfo, parentBookingRequest) {
        this.bookingsInfo = bookingsInfo
        this.selectedBookingsInfo = bookingsInfo
        if (!bookingsInfo.length) {
          this.selectedShelfBookingPrice = null
          this.selectedServices = []
          this.servicesStepCompleted = false
        }
        if (
            !bookingsInfo.length
            && bookingsInfo.bookingRequest !== parentBookingRequest
            && parseInt(this.$route.params.bookingRequestId) !== parentBookingRequest
        ) {
          this.$router.push({name: 'extend-booking-request', params: {bookingRequestId: parentBookingRequest}})
        }
      },

      getExtendBookingsInfo () {
        this.bookingsInfo = []
        let foundCurrentBookingRequest = false
        let currentShelfPeriodPrice = null
        const storageValue = localStorage.getItem("extendBookingsInfo")
        if (!storageValue) {
          return []
        }
        const bookingsInfo = JSON.parse(localStorage.getItem("extendBookingsInfo"))
        const now = new Date()
        for (const obj of bookingsInfo) {
          // compare the expiry time of the item with the current time
          if (now.getTime() < obj.expiresOn) {
            // If the item is expired, delete the item from storage
            // and return null
            this.bookingsInfo.push(obj)

            if (this.selectedBookingRequest.id === obj.bookingRequest || this.selectedBookingRequest.id === obj.parentBookingRequest) {
              foundCurrentBookingRequest = true
              currentShelfPeriodPrice = this.shelfPeriodPrices.find(spp => spp.id === obj.shelfPeriodPrice)
            }
          }
        }
        localStorage.setItem("extendBookingsInfo", JSON.stringify(this.bookingsInfo))
        if (foundCurrentBookingRequest && currentShelfPeriodPrice) {
          this.selectedBookingsInfo = this.bookingsInfo
          this.selectedShelfBookingPrice = currentShelfPeriodPrice
          this.servicesStepCompleted = true
        }
      },

      goToCheckoutDirectly () {
        this.selectedShelfBookingPrice = this.bookingsInfo.length ? this.bookingsInfo[0].shelfPeriodPrice : null
        this.selectedBookingsInfo = this.bookingsInfo
        this.servicesStepCompleted = true
      },

      getCartToken () {
        const existingToken = localStorage.getItem('extendCartToken');
        this.cartToken = existingToken || crypto.randomUUID();
        localStorage.setItem('extendCartToken', this.cartToken);
      },
    }
  }
</script>

<style scoped>

</style>